import { useEffect } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'
import { reportCard, reportCardSub, reportCardTitle, reportEyebrow } from '../detailUi'
import type { DetailSection } from './types'
import { DetailFields } from './DetailFields'

export function DetailSectionModal({ section, vehicleId, onClose }: {
  section: DetailSection
  vehicleId: string
  onClose: () => void
}): JSX.Element {
  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${section.title} · ${vehicleId}`}
        className={clsx(
          reportCard,
          'flex max-h-[min(88vh,720px)] w-full max-w-xl flex-col overflow-hidden animate-slide-up',
        )}
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-slate-200/80 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className={reportEyebrow}>{vehicleId}</div>
            <h2 className={clsx(reportCardTitle, 'mt-1 truncate')}>{section.title}</h2>
            {section.subtitle && (
              <p className={clsx(reportCardSub, 'mt-0.5')}>{section.subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto">
          {section.body && (
            <p className="border-b border-slate-200 px-6 py-4 text-sm leading-relaxed text-slate-600">
              {section.body}
            </p>
          )}
          {section.lines && section.lines.length > 0 && (
            <ul className="divide-y divide-slate-200 border-b border-slate-200">
              {section.lines.map((line) => (
                <li key={line} className="px-6 py-3 text-sm text-slate-600 hover:bg-slate-50">
                  {line}
                </li>
              ))}
            </ul>
          )}
          {section.fields.length > 0 ? (
            <DetailFields fields={section.fields} variant="list" />
          ) : !section.body ? (
            <p className="px-6 py-8 text-center text-sm text-slate-500">No fields for this section.</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
